import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * First-time-visitor welcome nudge.
 *
 * A slim, dismissable onboarding moment that pulls a first-time visitor — often
 * arriving from an influencer's shared ?ref= link — toward their aha-moment
 * (their first entry) fast. Onboarding-CRO principles:
 *   - ONE clear next action (Browse prizes), no dead ends.
 *   - Never traps the user — always dismissable.
 *   - Never re-shown to returning users (localStorage flag).
 *
 * Layout: NOT a giant blocking modal. It renders as a bottom-sheet card on
 * mobile and a top-right corner card on desktop, so the page stays usable.
 *
 * Gating: a single localStorage flag (`wap_seen_welcome`). It is set when the
 * user dismisses OR acts on the primary CTA, so the nudge never re-shows.
 *
 * Compliance: skill-based copy only — never "lottery / gamble / guaranteed".
 * Mirrors the wording of the landing trust strip + how-it-works.
 */

export const WELCOME_SEEN_KEY = 'wap_seen_welcome';

/** True only for a genuine first-time visitor (flag not yet set). */
export function hasSeenWelcome(): boolean {
  try {
    return localStorage.getItem(WELCOME_SEEN_KEY) === '1';
  } catch {
    // Private mode / storage disabled — treat as "seen" so we never nag.
    return true;
  }
}

/** Persist the seen flag so the nudge never re-shows. Failure is non-fatal. */
export function markWelcomeSeen(): void {
  try {
    localStorage.setItem(WELCOME_SEEN_KEY, '1');
  } catch {
    /* storage unavailable — best effort */
  }
}

/** True when the OS/browser requests reduced motion. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

interface WelcomeNudgeProps {
  /**
   * Primary CTA handler. Should move the visitor toward their first entry
   * (e.g. scroll to the competitions list). The nudge marks itself seen and
   * closes after calling this.
   */
  onPrimaryAction: () => void;
  /** Optional href for the small secondary "How it works" link. */
  howItWorksHref?: string;
}

const STEP_ICONS = [
  // 1 — Pick a prize (gift)
  <path
    key="gift"
    strokeLinecap="round"
    strokeLinejoin="round"
    d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"
  />,
  // 2 — Enter fast (lightning bolt)
  <path
    key="bolt"
    strokeLinecap="round"
    strokeLinejoin="round"
    d="m3.75 13.5 10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75Z"
  />,
  // 3 — Winner announced (trophy)
  <path
    key="trophy"
    strokeLinecap="round"
    strokeLinejoin="round"
    d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1 3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
  />,
];

export function WelcomeNudge({ onPrimaryAction, howItWorksHref = '/how-it-works' }: WelcomeNudgeProps) {
  const { t } = useTranslation();
  // Render decision is taken once on mount so the nudge can't pop in/out as the
  // app re-renders. First-time visitors only.
  const [open, setOpen] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (hasSeenWelcome()) return;
    setOpen(true);
    if (prefersReducedMotion()) {
      // No slide-in — show immediately at rest.
      setAnimate(true);
      return;
    }
    // Next frame so the enter transition runs from the off-screen state.
    const id = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(id);
  }, []);

  if (!open) return null;

  const close = () => {
    markWelcomeSeen();
    setOpen(false);
  };

  const handlePrimary = () => {
    markWelcomeSeen();
    setOpen(false);
    onPrimaryAction();
  };

  const reduceMotion = prefersReducedMotion();
  // Slide up on mobile, slide in from the right on desktop. Reduced motion: no transform.
  const motionClasses = reduceMotion
    ? 'opacity-100'
    : [
        'transition-all duration-300 ease-out motion-reduce:transition-none',
        animate
          ? 'translate-y-0 sm:translate-x-0 opacity-100'
          : 'translate-y-4 sm:translate-y-0 sm:translate-x-6 opacity-0',
      ].join(' ');

  const steps = [t('welcome.step1'), t('welcome.step2'), t('welcome.step3')];

  return (
    <div
      role="region"
      aria-label={t('welcome.region_label')}
      data-testid="welcome-nudge"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-3 sm:inset-x-auto sm:bottom-auto sm:left-auto sm:right-4 sm:top-20 sm:px-0 sm:pb-0"
    >
      <div
        className={`pointer-events-auto mx-auto w-full max-w-md rounded-2xl p-[2px] shadow-card sm:mx-0 sm:w-[22rem] ${motionClasses}`}
        style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #f97316 100%)' }}
      >
        <div className="rounded-2xl bg-white p-4 sm:p-5">
          {/* Header row: trust line + dismiss */}
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm font-bold leading-snug text-violet-900">
              {t('welcome.trust_line')}
            </p>
            <button
              type="button"
              onClick={close}
              aria-label={t('welcome.dismiss_aria')}
              className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 3-step how-it-works micro-row */}
          <ol className="mt-3 space-y-2.5">
            {steps.map((label, i) => (
              <li key={i} className="flex items-center gap-3">
                <span
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white"
                  style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)' }}
                  aria-hidden="true"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                    {STEP_ICONS[i]}
                  </svg>
                </span>
                <span className="text-sm leading-snug text-gray-600">{label}</span>
              </li>
            ))}
          </ol>

          {/* ONE primary CTA + a quiet secondary link. No dead ends. */}
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={handlePrimary}
              className="inline-flex min-h-[44px] flex-1 cursor-pointer items-center justify-center rounded-button px-4 text-sm font-bold text-white shadow-sm transition hover:shadow-md focus:outline-none focus:ring-4 focus:ring-violet-300"
              style={{ background: 'linear-gradient(90deg, #7c3aed 0%, #ec4899 100%)' }}
            >
              {t('welcome.cta')}
            </button>
            <a
              href={howItWorksHref}
              onClick={markWelcomeSeen}
              className="inline-flex min-h-[44px] cursor-pointer items-center text-sm font-semibold text-violet-700 underline-offset-2 transition hover:text-violet-900 hover:underline focus:outline-none focus:ring-2 focus:ring-violet-300"
            >
              {t('welcome.how_link')}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WelcomeNudge;
